import { DatePipe, isPlatformBrowser } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, ErrorHandler, Inject, LOCALE_ID, NgModule, PLATFORM_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ExtraOptions, Router, RouterModule } from '@angular/router';

import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { ToastrModule } from 'ngx-toastr';

import * as firebase from 'firebase/app';
import 'firebase/remote-config';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';

import { routes } from './app.routes';

// Modules Imports
import { TooltipDirectiveModule } from '@toro/common/shared/directives/tooltip/tooltip.module';
import { TrSpinnerButtonDirectiveModule } from '@toro/common/shared/directives/tr-spinner-button/tr-spinner-button.module';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { ChatBlipModule } from '@toro/common/shared/components/chat-blip/chat-blip.module';
import { ChatOnboardingModule } from '@toro/common/shared/components/chat-onboarding/chat-onboarding.module';
import { ConfirmWithOptionsModule } from '@toro/common/shared/components/dialogs/cards/confirm-with-options/confirm-with-options.module';
import { ConfirmModule } from '@toro/common/shared/components/dialogs/confirm/confirm.module';
import { DialogReceiptModule } from '@toro/common/shared/components/dialogs/dialog-receipt/dialog-receipt.module';
import { SimpleModalModule } from '@toro/common/shared/components/dialogs/simple/simple.module';
import { HubAuthorizationModule } from '@toro/common/shared/components/hub-authorization/hub-authorization.module';
import { HubLoginModule } from '@toro/common/shared/components/hub-login/hub-login.module';
import { HubLogoModule } from '@toro/common/shared/components/hub-logo/hub-logo.module';
import { HubNavModule } from '@toro/common/shared/components/hub-nav/hub-nav.module';
import { SvgIconModule } from '@toro/common/shared/components/svg-icon/svg-icon.module';
import { TrSideMenuModule } from '@toro/common/shared/components/tr-side-menu/tr-side-menu.module';
import { TrWrapperTwoColumnsModule } from '@toro/common/shared/components/tr-wrapper-two-columns/tr-wrapper-left-column.module';
import { ZendeskHelpButtonModule } from '@toro/common/shared/components/zendesk-help-button/zendesk-help-button.module';
import { DialogGraphicModule } from '../app/components/dialogs/dialog-graphic/dialog-graphic.module';

import { ComponentsModule } from './components/components.module';
import { GlobalModule } from './global.module';
import { PartialsModule } from './partials/partials.module';
import { StockAnalysisModule } from './partials/stock-analysis/stock-analysis.module';
import { StockCardsModule } from './partials/stock-cards/stocks-cards.module';
import { StockModule } from './partials/stock/stock.module';

// Services Imports
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { LayerService } from '@toro/common/shared/services/LayerService';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { HttpService } from '@toro/common/shared/services/http/http.service';
import { InternetService } from '@toro/common/shared/services/internet/internet.service';
import { LoginWraperService } from '@toro/common/shared/services/login.wraper.service';
import { BackendToFrontendParserService } from '@toro/common/shared/services/parser/backend-to-frontend-parser.service';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';

import { FormsUtils } from '@toro/common/shared/resources/forms.utils';
import { AuthorizationWraperService } from '@toro/common/shared/services/authorization-wraper/authorization.wraper.service';
import { ComputerService } from '@toro/common/shared/services/computer/computer.service';
import { DateFormatService } from '@toro/common/shared/services/date-format/date-format.service';
import { OrdersService } from '@toro/common/shared/services/orders/orders.service';
import { PushNotificationService } from '@toro/common/shared/services/push-notification/push-notification.service';
import { RealtimeCommonService } from '@toro/common/shared/services/realtime/realtime-common.service';
import { RealtimeQuoteService } from '@toro/common/shared/services/realtime/realtime-quote.service';
import { RealtimeService } from '@toro/common/shared/services/realtime/realtime.service';
import { RouterTrackerService } from '@toro/common/shared/services/router-tracker/router-tracker.service';
import { TrackingService } from '@toro/common/shared/services/tracking/tracking.service';
import { UnauthorizedErrorHandlerModule } from '@toro/common/shared/services/unauthorized-error-handler/unauthorized-error-handler.module';
import { UserProfileService } from '@toro/common/shared/services/users/user-profile.service';

// Effects Imports
import { AgreementsEffects } from '@toro/common/shared/state-management/agreements/agreements.effects';
import { ApplicationMessagesEffects } from '@toro/common/shared/state-management/application-messages/application-message.effects';
import { AuthorizationEffects } from '@toro/common/shared/state-management/authorization/authorization.effects';
import { AveragePriceEffects } from '@toro/common/shared/state-management/average-price/average-price.effects';
import { EnvironmentEffects } from '@toro/common/shared/state-management/environment/environment.effects';
import { FinanceEffects } from '@toro/common/shared/state-management/finance/finance.effects';
import { InitializerEffects } from '@toro/common/shared/state-management/initializer/initializer.effects';
import { NotificationsEffects } from '@toro/common/shared/state-management/notifications/notifications.effects';
import { PlatformsAcceptanceEffects } from '@toro/common/shared/state-management/platforms-acceptance/platforms-acceptance.effects';
import { PosRiskEffects } from '@toro/common/shared/state-management/posrisk/posrisk.effects';
import { ResearchEffects } from '@toro/common/shared/state-management/research/research.effects';
import { SalesForceEffects } from '@toro/common/shared/state-management/sales-force/sales-force.effects';
import { StockResearchAdvicesEffects } from '@toro/common/shared/state-management/stock-research-advices/stock-research-advices.effects';
import { SuitabilityEffects } from '@toro/common/shared/state-management/suitability/suitability.effects';
import { ToastsEffects } from '@toro/common/shared/state-management/toasts/toasts.effects';
import { TrackingEffects } from '@toro/common/shared/state-management/tracking/tracking.effects';
import { UserProfileEffects } from '@toro/common/shared/state-management/userprofile/userprofile.effects';
import { UsersEffects } from '@toro/common/shared/state-management/users/users.effects';
import { UtilsEffects } from '@toro/common/shared/state-management/utils/utils.effects';
import { TraderEffects } from './state-management/club-trader/trader.effects';
import { SuitabilityBypassAgreementEffects } from './state-management/suitability-bypass-agreement/suitability-bypass-agreement.effects';
import { AdvisorEffects } from '@toro/common/shared/state-management/advisor/advisor.effects';

// Migrar
import { DigitalSubscriptionEffects } from '@toro/common/shared/state-management/digital-subscription/digital-subscription.effects';
import { InfoproductsEffects } from '@toro/common/shared/state-management/infoproducts/infoproducts.effects';
import { TraderAdvisorEffects } from '@toro/common/shared/state-management/trader-advisor/trader-advisor.effects';
import { AdvancedPlatformsEffects } from './state-management/advanced-platforms/advanced-platforms.effects';
import { BillingEffects } from './state-management/billing/billing.effects';
import { ExchangeEffects } from './state-management/exchange/exchange.effects';
import { RequestSinacorNoteEffects } from './state-management/report-sinacor/report-sinacor.effects';
import { StocksExploreMarketEffects } from './state-management/stock-explore-market/stock-explore-market.effects';
import { StockIpoOfferingEffects } from './state-management/stock-ipo-offering/stock-ipo-offering.effects';
import { StockIpoReservationEffects } from './state-management/stock-ipo-reservation/stock-ipo-reservation.effects';
import { StockResearchTrendEffects } from './state-management/stock-research-trend/stock-research-trend.effects';
import { StockEffects } from './state-management/stock/stock.effects';
import { StocksExchangeCheckoutEffects } from './state-management/stocks-exchange-checkout/stocks-exchange-checkout.effects';
import { StocksWalletExchangeCheckoutEffects } from './state-management/stocks-wallet-exchange-checkout/stocks-wallet-exchange-checkout.effects';

// Reducers Imports
import { AgreementsReducer } from '@toro/common/shared/state-management/agreements/agreements.reducer';
import { ApplicationMessagesReducer } from '@toro/common/shared/state-management/application-messages/application-message.reducer';
import { AuthorizationReducer } from '@toro/common/shared/state-management/authorization/authorization.reducer';
import { AveragePriceReducer } from '@toro/common/shared/state-management/average-price/average-price.reducer';
import { DigitalSubscriptionReducer } from '@toro/common/shared/state-management/digital-subscription/digital-subscription.reducer';
import { EnvironmentReducer } from '@toro/common/shared/state-management/environment/environment.reducer';
import { FinanceReducer } from '@toro/common/shared/state-management/finance/finance.reducer';
import { InfoproductsReducer } from '@toro/common/shared/state-management/infoproducts/infoproducts.reducer';
import { NotificationsReducer } from '@toro/common/shared/state-management/notifications/notifications.reducer';
import { PlatformsAcceptanceReducer } from '@toro/common/shared/state-management/platforms-acceptance/platforms-acceptance.reducer';
import { PosRiskReducer } from '@toro/common/shared/state-management/posrisk/posrisk.reducer';
import { ResearchReducer } from '@toro/common/shared/state-management/research/research.reducer';
import { SalesForceReducer } from '@toro/common/shared/state-management/sales-force/sales-force.reducer';
import { StockResearchAdivcesReducer } from '@toro/common/shared/state-management/stock-research-advices/stock-research-advices.reducer';
import { StructuredProductReducer } from '@toro/common/shared/state-management/structured-products/structured-products.reducer';
import { SuitabilityReducer } from '@toro/common/shared/state-management/suitability/suitability.reducer';
import { ToastsReducer } from '@toro/common/shared/state-management/toasts/toasts.reducer';
import { TraderAdvisorReducer } from '@toro/common/shared/state-management/trader-advisor/trader-advisor.reducer';
import { UserProfileReducer } from '@toro/common/shared/state-management/userprofile/userprofile.reducer';
import { UsersReducer } from '@toro/common/shared/state-management/users/users.reducer';
import { UtilsReducer } from '@toro/common/shared/state-management/utils/utils.reducer';
import { AdvancedPlatformsReducer } from './state-management/advanced-platforms/advanced-platforms.reducer';
import { BillingReducer } from './state-management/billing/billing.reducer';
import { traderReducer as TraderReducer } from './state-management/club-trader/trader.reducer';
import { ExchangeReducer } from './state-management/exchange/exchange.reducer';
import { GlobalsReducer } from './state-management/globals/globals.reducer';
import { RequestSinacorNoteReducer } from './state-management/report-sinacor/report-sinacor.reducer';
import { StocksExploreMarketReducer } from './state-management/stock-explore-market/stock-explore-market.reducer';
import { StockIpoOfferingReducer } from './state-management/stock-ipo-offering/stock-ipo-offering.reducer';
import { StockIpoReservationReducer } from './state-management/stock-ipo-reservation/stock-ipo-reservation.reducer';
import { StockResearchTrendReducer } from './state-management/stock-research-trend/stock-research-trend.reducer';
import { StockReducer } from './state-management/stock/stock.reducer';
import { StocksExchangeCheckoutReducer } from './state-management/stocks-exchange-checkout/stocks-exchange-checkout.reducer';
import { SuitabilityBypassAgreementReducer } from './state-management/suitability-bypass-agreement/suitability-bypass-agreement.reducer';
import { AdvisorReducer } from '@toro/common/shared/state-management/advisor/advisor.reducer';

// Pipes Imports
import { LoggedDomainGuard } from '@toro/common/shared/guards/logged-domain.guard';
import { AuthorizationInterceptor } from '@toro/common/shared/interceptors/authorization.interceptor';
import { FirstNamePipeModule } from '@toro/common/shared/pipes';
import { AppcuesModule } from '@toro/common/shared/services/appcues/appcues.module';
import { SentryService } from '@toro/common/shared/services/sentry/sentry.service';
import { LiveStreamEffects } from '@toro/common/shared/state-management/live-stream/live-stream.effects';
import { LiveStreamReducer } from '@toro/common/shared/state-management/live-stream/live-stream.reducer';
import { RewardsModule } from '../app/components/dialogs/rewards/rewards.module';
import { TradingViewGuard } from './guards/trading-view.guard';
import { MyCapitalReducer } from '@toro/common/shared/state-management/mycapital/mycapital.reducer';
import { MyCapitalEffects } from '@toro/common/shared/state-management/mycapital/mycapital.effects';
import { GuidedTourModule, GuidedTourService } from 'ngx-guided-tour';
import { StorageService } from '@toro/common/libs/toro-storage';
import { SidePanelModule } from './shared/features/side-panel/side-panel.module';
import { FeatureFlagDirectiveModule } from './shared/directives/feature-flag/feature-flag.module';
import { FeatureFlagsService } from './services/feature-flags/feature-flags.service';

// Sentry
import * as Sentry from '@sentry/angular-ivy';

export function loadSettings(service: SettingsService) {
    return () => service.loadEnvironmentSettings();
}

export function initializeFeatureFlags(featureFlagsService: FeatureFlagsService) {
    return () => featureFlagsService.initialize();
}

// Inicializar Firebase
if (!firebase.apps.length) {
    firebase.initializeApp(environment.firebase);
}

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'top', // rolar a janela para o topo
  anchorScrolling: 'enabled', // permite rolar para âncoras
};

@NgModule({
    imports: [
        ComponentsModule,
        GlobalModule,
        PartialsModule,
        StockModule,
        StockCardsModule,
        StockAnalysisModule,
        BrowserAnimationsModule,
        BrowserModule.withServerTransition({ appId: 'hub-stock' }),
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        RouterModule,
        RouterModule.forRoot(routes, routerOptions),
        TooltipDirectiveModule,
        ChatBlipModule,
        ChatOnboardingModule,
        ZendeskHelpButtonModule,
        HubNavModule,
        HubLogoModule,
        HubLoginModule,
        HubAuthorizationModule,
        TrSideMenuModule,
        TrWrapperTwoColumnsModule,
        MatDialogModule,
        MatOptionModule,
        SvgIconModule,
        FirstNamePipeModule,
        TrSpinnerButtonDirectiveModule,
        ConfirmWithOptionsModule,
        DialogReceiptModule,
        DialogGraphicModule,
        ConfirmModule,
        SimpleModalModule,
        UnauthorizedErrorHandlerModule,
        TypeaheadModule.forRoot(),
        ToastrModule.forRoot({
            timeOut: 0,
            maxOpened: 1,
        }),
        StoreModule.forRoot({
            advancedPlatforms: AdvancedPlatformsReducer,
            advices: StockResearchAdivcesReducer,
            agreements: AgreementsReducer,
            platformsAcceptance: PlatformsAcceptanceReducer,
            applicationMessages: ApplicationMessagesReducer,
            authentication: UsersReducer,
            authorization: AuthorizationReducer,
            billing: BillingReducer,
            digitalSubscription: DigitalSubscriptionReducer,
            infoproducts: InfoproductsReducer,
            environment: EnvironmentReducer,
            exchange: ExchangeReducer,
            exchangeCheckout: StocksExchangeCheckoutReducer,
            exploreMarket: StocksExploreMarketReducer,
            finance: FinanceReducer,
            globals: GlobalsReducer,
            liveStream: LiveStreamReducer,
            traderAdvisor: TraderAdvisorReducer,
            advisor: AdvisorReducer,
            notifications: NotificationsReducer,
            posrisk: PosRiskReducer,
            structuredProducts: StructuredProductReducer,
            research: ResearchReducer,
            salesforce: SalesForceReducer,
            stocks: StockReducer,
            suitability: SuitabilityReducer,
            toasts: ToastsReducer,
            trends: StockResearchTrendReducer,
            userProfile: UserProfileReducer,
            utils: UtilsReducer,
            suitabilityBypassAgreement: SuitabilityBypassAgreementReducer,
            stockIpoOffering: StockIpoOfferingReducer,
            stockIpoReservation: StockIpoReservationReducer,
            requestSinacorNote: RequestSinacorNoteReducer,
            profileTrader: TraderReducer,
            custodyAveragePrice: AveragePriceReducer,
            myCapitalKeys: MyCapitalReducer,
        }),
        environment.production
            ? []
            : StoreDevtoolsModule.instrument({ maxAge: 1120 }),
        EffectsModule.forRoot([
            AdvancedPlatformsEffects,
            AgreementsEffects,
            PlatformsAcceptanceEffects,
            ApplicationMessagesEffects,
            BillingEffects,
            SuitabilityEffects,
            PosRiskEffects,
            UsersEffects,
            UtilsEffects,
            StocksExploreMarketEffects,
            ResearchEffects,
            InitializerEffects,
            InfoproductsEffects,
            ToastsEffects,
            AuthorizationEffects,
            NotificationsEffects,
            StocksExchangeCheckoutEffects,
            StocksWalletExchangeCheckoutEffects,
            StockResearchTrendEffects,
            StockResearchAdvicesEffects,
            ExchangeEffects,
            FinanceEffects,
            TrackingEffects,
            StockEffects,
            DigitalSubscriptionEffects,
            InfoproductsEffects,
            UserProfileEffects,
            EnvironmentEffects,
            LiveStreamEffects,
            TraderAdvisorEffects,
            AdvisorEffects,
            SalesForceEffects,
            SuitabilityBypassAgreementEffects,
            StockIpoOfferingEffects,
            StockIpoReservationEffects,
            RequestSinacorNoteEffects,
            TraderEffects,
            AveragePriceEffects,
            MyCapitalEffects,
        ]),
        AppcuesModule,
        RewardsModule,
        GuidedTourModule,
        SidePanelModule,
        FeatureFlagDirectiveModule,
    ],
    declarations: [
        AppComponent,
    ],
    providers: [
        SettingsService,
        // <-- CONFIGURAÇÕES DO SENTRY -->
        {
          provide: ErrorHandler,
          useValue: Sentry.createErrorHandler({
            showDialog: false,
          }),
        },
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        {
          provide: APP_INITIALIZER,
          useFactory: () => () => {},
          deps: [Sentry.TraceService],
          multi: true,
        },
        { provide: APP_INITIALIZER, useFactory: loadSettings, deps: [SettingsService], multi: true },
        { provide: APP_INITIALIZER, useFactory: initializeFeatureFlags, deps: [FeatureFlagsService], multi: true },
        { provide: LOCALE_ID, useValue: 'pt' },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthorizationInterceptor,
            multi: true,
        },
        LayerService,
        LoginWraperService,
        DialogsService,
        MatDialog,
        InternetService,
        HttpService,
        BackendToFrontendParserService,
        RealtimeService,
        RealtimeQuoteService,
        RealtimeCommonService,
        DateFormatService,
        PosRiskEffects,
        RouterTrackerService,
        PushNotificationService,
        FormsUtils,
        AuthorizationWraperService,
        ComputerService,
        DatePipe,
        OrdersService,
        UserProfileService,
        TrackingService,
        LoggedDomainGuard,
        SentryService,
        TradingViewGuard,
        GuidedTourService,
        StorageService,
    ],
    entryComponents: [],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        @Inject(APP_ID) private appId: string) {
            const platform = isPlatformBrowser(platformId) ? 'in the browser' : 'on the server';
            // tslint:disable-next-line: no-console
            console.log(`Running ${platform} with appId=${appId}`);
    }
}
